import { useState, useEffect, useCallback } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import {
  Activity as ActivityIcon,
  Search,
  Download,
  Filter,
  Eye,
  Calendar,
  User,
  Building,
  FileText,
  Phone,
  Mail,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreHorizontal,
  Users,
  BarChart3,
  Globe,
} from "lucide-react"

import { StatsCard } from "@crm-src/components/stats-card"
import { StatsCardsSkeleton } from "@crm-src/components/stats-cards-skeleton"
import { EnhancedAdminGuard } from "@crm-src/components/enhanced-admin-guard"
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination"
import {
  systemActivitiesApi,
  ACTIVITY_TYPE_LABELS,
  ACTIVITY_STATUS_LABELS
} from "@crm-src/lib/system-activities-api"
import type {
  SystemActivity,
  SystemActivityFilters,
} from "@crm-src/lib/system-activities-api"
import { useUsers } from "@crm-src/hooks/useUsers"

function SystemActivityContent() {
  const { toast } = useToast()
  const { users, loading: isLoadingUsers } = useUsers()

  // State management
  const [activities, setActivities] = useState<SystemActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState<SystemActivity | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  // Per-row actions menu anchor — replaces the shadcn DropdownMenu.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string | number } | null>(null)

  // Filter state
  const [filters, setFilters] = useState<SystemActivityFilters>({
    page: 1,
    per_page: 25
  })

  // Pagination state
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 25,
    has_next: false,
    has_prev: false
  })

  // Summary state
  const [activitySummary, setActivitySummary] = useState<Record<string, number>>({})
  const [userSummary, setUserSummary] = useState<Record<string, number>>({})

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    setLoading(true)
    try {
      const currentFilters = {
        ...filters,
        search: searchTerm || undefined,
      }

      const response = await systemActivitiesApi.getActivities(currentFilters)
      setActivities(response.data)
      setPagination(response.meta.pagination)
      setActivitySummary(response.meta.activity_summary || {})
      setUserSummary(response.meta.user_summary || {})
    } catch (error) {
      console.error("Failed to fetch system activities:", error)
      toast({
        title: "Error",
        description: "Failed to load system activities",
        variant: "destructive",
      })
      // Set empty data on error
      setActivities([])
      setPagination({
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: 25,
        has_next: false,
        has_prev: false
      })
    } finally {
      setLoading(false)
    }
  }, [filters, searchTerm, toast])

  // Load activities on mount and when filters change
  useEffect(() => {
    fetchActivities()
  }, [fetchActivities])

  // Debounce search input
  useEffect(() => {
    if (searchInput !== searchTerm) {
      setIsSearching(true)
    }

    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
      setFilters(prev => ({ ...prev, page: 1 })) // Reset to first page when searching
      setIsSearching(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput, searchTerm])

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  // Handle filter changes
  const handleFilterChange = (key: keyof SystemActivityFilters, value: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      page: 1 // Reset to first page when filtering
    }))
  }

  // Clear all filters
  const clearFilters = () => {
    setFilters({ page: 1, per_page: 25 })
    setSearchInput("")
    setSearchTerm("")
  }

  // Handle CSV export
  const handleExportCSV = async () => {
    try {
      const exportFilters = {
        ...filters,
        search: searchTerm || undefined,
      }
      await systemActivitiesApi.exportActivities(exportFilters)
      toast({
        title: "Success",
        description: "System activities exported successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export system activities",
        variant: "destructive",
      })
    }
  }

  // Open view dialog
  const openViewDialog = (activity: SystemActivity) => {
    setSelectedActivity(activity)
    setIsViewDialogOpen(true)
  }

  // Get activity icon
  const getActivityIcon = (activityType: string) => {
    switch (activityType) {
      case 'call': return <Phone size={16} />
      case 'email': return <Mail size={16} />
      case 'meeting': return <Users size={16} />
      case 'note': return <MessageSquare size={16} />
      case 'task': return <CheckCircle size={16} />
      case 'customer_created':
      case 'lead_created':
      case 'contact_created': return <User size={16} />
      default: return <ActivityIcon size={16} />
    }
  }

  // Status chip styling — maps a status to a soft-tinted Chip sx (was a
  // Tailwind bg/text class pair on the shadcn Badge).
  const getStatusSx = (status: string): SxProps<Theme> => {
    switch (status) {
      case 'completed': return { bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: "success.dark" }
      case 'in_progress': return { bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: "primary.dark" }
      case 'scheduled': return { bgcolor: (t) => alpha(t.palette.warning.main, 0.15), color: "warning.dark" }
      case 'cancelled': return { bgcolor: (t) => alpha(t.palette.error.main, 0.12), color: "error.dark" }
      case 'no_show': return { bgcolor: "action.hover", color: "text.secondary" }
      default: return { bgcolor: "action.hover", color: "text.secondary" }
    }
  }

  // Priority chip styling — same idea as status.
  const getPrioritySx = (priority?: string): SxProps<Theme> => {
    switch (priority) {
      case 'urgent': return { bgcolor: (t) => alpha(t.palette.error.main, 0.12), color: "error.dark" }
      case 'high': return { bgcolor: (t) => alpha(t.palette.warning.main, 0.12), color: "warning.dark" }
      case 'medium': return { bgcolor: (t) => alpha(t.palette.warning.main, 0.15), color: "warning.dark" }
      case 'low': return { bgcolor: "action.hover", color: "text.secondary" }
      default: return { bgcolor: "action.hover", color: "text.secondary" }
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="System Activity"
        description="Monitor user activities and system events"
        actions={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Download size={16} />}
            onClick={handleExportCSV}
          >
            Export
          </Button>
        }
      />

      {/* Stats Cards */}
      {!loading ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
          <StatsCard
            title="Total Activities"
            value={pagination.total_count}
            icon={ActivityIcon}
            iconColor="text-blue-600"
          />
          <StatsCard
            title="Active Users"
            value={Object.keys(userSummary).length}
            icon={Users}
            iconColor="text-green-600"
            valueColor="text-green-600"
          />
          <StatsCard
            title="Activity Types"
            value={Object.keys(activitySummary).length}
            icon={BarChart3}
            iconColor="text-purple-600"
            valueColor="text-purple-600"
          />
          <StatsCard
            title="Completed Today"
            value={activitySummary['completed'] || 0}
            icon={CheckCircle}
            iconColor="text-emerald-600"
            valueColor="text-emerald-600"
          />
        </Box>
      ) : (
        <StatsCardsSkeleton />
      )}

      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, gap: 2 }}>
              {/* Search */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search activities..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  sx={{ maxWidth: 384, width: "100%" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} color="#9ca3af" />
                      </InputAdornment>
                    ),
                  }}
                />
                {isSearching && (
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary", animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } } }}>
                    Searching...
                  </Typography>
                )}
              </Box>

              {/* Filters */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ color: "text.secondary", display: "flex" }}><Filter size={16} /></Box>

                <TextField
                  select
                  size="small"
                  label="All Users"
                  value={filters.user_id || "all"}
                  onChange={(e) => handleFilterChange('user_id', e.target.value === 'all' ? undefined : e.target.value)}
                  sx={{ width: 180 }}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id.toString()}>
                      {user.full_name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="All Types"
                  value={filters.activity_type || "all"}
                  onChange={(e) => handleFilterChange('activity_type', e.target.value === 'all' ? undefined : e.target.value)}
                  sx={{ width: 180 }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {Object.entries(ACTIVITY_TYPE_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="All Status"
                  value={filters.status || "all"}
                  onChange={(e) => handleFilterChange('status', e.target.value === 'all' ? undefined : e.target.value)}
                  sx={{ width: 180 }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {Object.entries(ACTIVITY_STATUS_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>

                {(filters.user_id || filters.activity_type || filters.status || searchTerm) && (
                  <Button variant="outlined" color="inherit" size="small" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                )}
              </Box>
            </Box>
          }
        />

        <CardContent>
          {loading ? (
            <PageLoader />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Activity</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Related To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: "center", color: "text.secondary" }}>
                        No activities found
                      </TableCell>
                    </TableRow>
                  ) : (
                    activities.map((activity) => (
                      <TableRow
                        key={activity.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => openViewDialog(activity)}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box sx={{ flexShrink: 0, display: "flex" }}>
                              {getActivityIcon(activity.activity_type)}
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 500 }}>{activity.title}</Typography>
                              {activity.description && (
                                <Typography sx={{ fontSize: 14, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>
                                  {activity.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ color: "text.secondary", display: "flex" }}><User size={16} /></Box>
                            <Typography component="span" sx={{ fontWeight: 500 }}>
                              {activity.user ? activity.user.full_name : 'System'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip variant="outlined" size="small" label={ACTIVITY_TYPE_LABELS[activity.activity_type] || activity.activity_type} />
                        </TableCell>
                        <TableCell>
                          {activity.customer && (
                            <Box sx={{ fontSize: 14 }}>
                              <Typography sx={{ fontWeight: 500 }}>{activity.customer.company_name || activity.customer.full_name}</Typography>
                              <Typography sx={{ color: "text.secondary" }}>{activity.customer.customer_id}</Typography>
                            </Box>
                          )}
                          {activity.customer_deal && (
                            <Box sx={{ fontSize: 14 }}>
                              <Typography sx={{ fontWeight: 500 }}>{activity.customer_deal.title}</Typography>
                              {activity.customer_deal.amount && (
                                <Typography sx={{ color: "text.secondary" }}>${activity.customer_deal.amount.toLocaleString()}</Typography>
                              )}
                            </Box>
                          )}
                          {activity.invoice && (
                            <Box sx={{ fontSize: 14 }}>
                              <Typography sx={{ fontWeight: 500 }}>{activity.invoice.invoice_number}</Typography>
                              {activity.invoice.amount && (
                                <Typography sx={{ color: "text.secondary" }}>${activity.invoice.amount.toLocaleString()}</Typography>
                              )}
                            </Box>
                          )}
                          {!activity.customer && !activity.customer_deal && !activity.invoice && (
                            <Typography component="span" sx={{ color: "text.secondary", fontSize: 14 }}>System</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={ACTIVITY_STATUS_LABELS[activity.status] || activity.status} sx={getStatusSx(activity.status)} />
                        </TableCell>
                        <TableCell>
                          {activity.task_priority && (
                            <Chip size="small" label={activity.task_priority} sx={getPrioritySx(activity.task_priority ?? undefined)} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ fontSize: 14 }}>
                            <Box>{new Date(activity.created_at).toLocaleDateString()}</Box>
                            <Typography sx={{ color: "text.secondary" }}>
                              {new Date(activity.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            sx={{ width: 32, height: 32 }}
                            aria-label="Open menu"
                            onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, id: activity.id }) }}
                          >
                            <MoreHorizontal size={16} />
                          </IconButton>
                          <Menu
                            anchorEl={menuAnchor?.id === activity.id ? menuAnchor.el : null}
                            open={menuAnchor?.id === activity.id}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                                                      // The Menu portals into <body>, but React events bubble
                            // through the REACT tree, so every click inside it — the
                            // item, the padding around it, the click-away backdrop —
                            // also reached the TableRow's onClick and opened the
                            // activity dialog. The IconButton stops it for the
                            // trigger only; the popup needs its own.
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MenuItem onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor(null)
                              openViewDialog(activity)
                            }}>
                              <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                              View Details
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <Pagination
            {...paginationPropsFromMeta(pagination)}
            entityNoun="activities"
            hideIfSinglePage
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* View Activity Details Dialog */}
      <Dialog open={isViewDialogOpen} onClose={() => setIsViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.5rem" }}>
            {selectedActivity && getActivityIcon(selectedActivity.activity_type)}
            Activity Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Detailed information about the selected activity
          </DialogContentText>

          {selectedActivity && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 1 }}>
              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <Box>
                  <Typography component="h3" sx={{ fontSize: "1.25rem", fontWeight: 600 }}>{selectedActivity.title}</Typography>
                  {selectedActivity.description && (
                    <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
                      {selectedActivity.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip size="small" label={ACTIVITY_STATUS_LABELS[selectedActivity.status] || selectedActivity.status} sx={getStatusSx(selectedActivity.status)} />
                  {selectedActivity.task_priority && (
                    <Chip size="small" label={`${selectedActivity.task_priority} priority`} sx={getPrioritySx(selectedActivity.task_priority ?? undefined)} />
                  )}
                </Box>
              </Box>

              {/* Activity Information */}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography component="h4" sx={{ fontWeight: 600, fontSize: 14, color: "text.secondary", mb: 1.5 }}>ACTIVITY INFORMATION</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><ActivityIcon size={16} /></Box>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Type</Typography>
                          <Typography sx={{ fontSize: 14 }}>{ACTIVITY_TYPE_LABELS[selectedActivity.activity_type] || selectedActivity.activity_type}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><User size={16} /></Box>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Performed By</Typography>
                          <Typography sx={{ fontSize: 14 }}>{selectedActivity.user.full_name}</Typography>
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{selectedActivity.user.email}</Typography>
                        </Box>
                      </Box>

                      {/* Where a system-written row came from. crm-api records
                          the request's IP and user agent in metadata; that is
                          the part of an audit trail you actually go looking
                          for, so it gets a row of its own rather than living
                          only in the API response. */}
                      {typeof selectedActivity.metadata?.ip_address === "string" && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }} data-testid="activity-origin">
                          <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><Globe size={16} /></Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Origin</Typography>
                            <Typography sx={{ fontSize: 14 }}>{selectedActivity.metadata.ip_address as string}</Typography>
                            {typeof selectedActivity.metadata?.user_agent === "string" && (
                              <Typography sx={{ fontSize: 12, color: "text.secondary", wordBreak: "break-word" }}>
                                {selectedActivity.metadata.user_agent as string}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}

                      {selectedActivity.duration_minutes && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><Clock size={16} /></Box>
                          <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Duration</Typography>
                            <Typography sx={{ fontSize: 14 }}>{selectedActivity.duration_minutes} minutes</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography component="h4" sx={{ fontWeight: 600, fontSize: 14, color: "text.secondary", mb: 1.5 }}>RELATED INFORMATION</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {selectedActivity.customer && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><Building size={16} /></Box>
                          <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Customer</Typography>
                            <Typography sx={{ fontSize: 14 }}>{selectedActivity.customer.company_name || selectedActivity.customer.full_name}</Typography>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>ID: {selectedActivity.customer.customer_id}</Typography>
                          </Box>
                        </Box>
                      )}

                      {selectedActivity.customer_deal && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><FileText size={16} /></Box>
                          <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Deal</Typography>
                            <Typography sx={{ fontSize: 14 }}>{selectedActivity.customer_deal.title}</Typography>
                            {selectedActivity.customer_deal.amount && (
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>${selectedActivity.customer_deal.amount.toLocaleString()}</Typography>
                            )}
                          </Box>
                        </Box>
                      )}

                      {selectedActivity.invoice && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><FileText size={16} /></Box>
                          <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Invoice</Typography>
                            <Typography sx={{ fontSize: 14 }}>{selectedActivity.invoice.invoice_number}</Typography>
                            {selectedActivity.invoice.amount && (
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>${selectedActivity.invoice.amount.toLocaleString()}</Typography>
                            )}
                          </Box>
                        </Box>
                      )}

                      {selectedActivity.location && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><Calendar size={16} /></Box>
                          <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Location</Typography>
                            <Typography sx={{ fontSize: 14 }}>{selectedActivity.location}</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Timestamps */}
              <Box sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Typography component="h4" sx={{ fontWeight: 600, fontSize: 14, color: "text.secondary", mb: 1.5 }}>TIMESTAMPS</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, fontSize: 14 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><Calendar size={16} /></Box>
                    <Box>
                      <Typography sx={{ fontWeight: 500 }}>Created</Typography>
                      <Typography sx={{ color: "text.secondary" }}>
                        {new Date(selectedActivity.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><Calendar size={16} /></Box>
                    <Box>
                      <Typography sx={{ fontWeight: 500 }}>Last Updated</Typography>
                      <Typography sx={{ color: "text.secondary" }}>
                        {new Date(selectedActivity.updated_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  {selectedActivity.due_date && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><AlertCircle size={16} /></Box>
                      <Box>
                        <Typography sx={{ fontWeight: 500 }}>Due Date</Typography>
                        <Typography sx={{ color: "text.secondary" }}>
                          {new Date(selectedActivity.due_date).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default function SystemActivityPage() {
  return (
    <EnhancedAdminGuard
      requiredPermissions={['admin:access']}
      redirectTo="/crm"
    >
      <SystemActivityContent />
    </EnhancedAdminGuard>
  )
}
